import React, { useState } from 'react';
import styled from 'styled-components';
import TextButton from 'components/inputs/TextButton';
import Input from 'components/inputs/Input';
import toast from 'react-hot-toast';
import { useCommuteGetCommuteTime } from 'api/commute/commute';
import { CommuteOptionDto } from 'api/model';
import {
  formatDateTime,
  getHoursAndMinutesFromMillisecondsString,
  toInputDateTimeString,
} from 'utils/date-helpers';
import TooltipInfo from 'components/TooltipInfo';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBus,
  faCar,
  faClose,
  faPersonWalking,
  faSubway,
} from '@fortawesome/free-solid-svg-icons';
import Dropdown from 'components/Dropdown';
import { googleMapsRoutingBaseURL } from 'constants/AppConstants';
import { replaceAllTransportationModeNames } from './googleMapsRouteHelper';

const MyTextButton = styled(TextButton)`
  padding: 0;
`;

const StepDiv = styled.div`
  font-size: 12px;
  color: #666;
  margin-top: 4px;
`;

const TravelOptionWrapper = styled.div`
  &:hover {
    background-color: #f9f9f9;
  }

  border: 1px solid #eee;
  padding: 6px;
  border-radius: 4px;
  justify-content: flex-start;
  align-items: flex-start;

  display: grid;
  grid-template-columns: 250px 250px 1fr;
  gap: 8px;
`;

const TravelOptionStepsWrapper = styled.div`
  display: grid;
  grid-template-columns: 50px 50px 1fr 30px;
  gap: 8px;
`;

const CommuteTimePickerContent = styled.div`
  min-width: 400px;
  padding: 12px;
`;

const CommuteTimePickerOptionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 400px;
  overflow: auto;
  gap: 8px;
`;

type AggregateStep = {
  travelMode?: string | null;
  durationSeconds?: number | null;
  distanceMeters?: number | null;
  departureTimeEpoch?: number | null;
  arrivalTimeEpoch?: number | null;
  instruction?: React.ReactNode[];
};

type Props = {
  fromAddress: string | undefined;
  toAddress: string | undefined;
  onSelectOption?: (
    option: CommuteOptionDto,
    fromAddress: string | undefined,
    toAddress: string | undefined
  ) => void;
  // optional estimated start time of the main assignment (ISO string)
  estimatedStartTime?: string | null;
  // optional estimated duration of the main assignment (milliseconds)
  estimatedDuration?: string | null;
  // if true, this picker is used for a personal transport TO the main assignment
  // and arrival must not be later than estimatedStartTime
  personalTransportToMain?: boolean;
  // if true, this picker is used for a personal transport FROM the main assignment
  // and departure must not be earlier than estimatedStartTime + estimatedDuration
  personalTransportFromMain?: boolean;
};

const CommuteTimePicker: React.FC<Props> = ({
  fromAddress,
  toAddress,
  onSelectOption,
  estimatedStartTime,
  estimatedDuration,
  personalTransportToMain,
  personalTransportFromMain,
}) => {
  // helper: format seconds -> human text
  const formatDuration = (secs?: number | null) => {
    if (!secs && secs !== 0) return '';
    const m = Math.round((secs ?? 0) / 60);
    return `${m} min`;
  };

  const getModeEmoji = (mode?: string | null) => {
    if (!mode) return '';
    const m = (mode ?? '').toLowerCase();
    if (m.includes('walk')) return <FontAwesomeIcon icon={faPersonWalking} />;
    if (m.includes('tram') || m.includes('rail') || m.includes('train'))
      return <FontAwesomeIcon icon={faSubway} />;
    if (m.includes('subway') || m.includes('metro'))
      return <FontAwesomeIcon icon={faSubway} />;
    if (m.includes('bus')) return <FontAwesomeIcon icon={faBus} />;
    if (m.includes('drive') || m.includes('driving'))
      return <FontAwesomeIcon icon={faCar} />;
    return '';
  };

  const getModeName = (mode?: string | null) => {
    if (!mode) return '';
    const m = (mode ?? '').toLowerCase();
    switch (m) {
      case 'walk':
        return 'Gå';
      case 'tram':
        return 'Ta spårvagn';
      case 'subway':
      case 'metro':
        return 'Ta tunnelbana';
      case 'bus':
        return 'Ta buss';
      case 'drive':
      case 'driving':
        return 'Kör';
      default:
        break;
    }
    return '';
  };

  // format a step's start—end time and duration, falling back when one side is missing
  const formatStepTimeRange = (step?: any) => {
    if (!step) return '';
    const dur = step.durationSeconds || 0;
    const startEpoch =
      step.departureTimeEpoch ??
      (step.arrivalTimeEpoch ? step.arrivalTimeEpoch - dur : undefined);
    const endEpoch =
      step.arrivalTimeEpoch ??
      (step.departureTimeEpoch ? step.departureTimeEpoch + dur : undefined);
    const start = startEpoch ? new Date(startEpoch * 1000) : null;
    const end = endEpoch ? new Date(endEpoch * 1000) : null;
    if (start && end)
      return `${start.toLocaleTimeString()} — ${end.toLocaleTimeString()} ${formatDuration(dur)}`;
    if (start) return `${start.toLocaleTimeString()} ${formatDuration(dur)}`;
    if (end) return `${end.toLocaleTimeString()} ${formatDuration(dur)}`;
    return formatDuration(dur);
  };

  // Merge consecutive steps that have the same travelMode into one aggregated step
  const aggregateSteps = (steps?: Array<AggregateStep>) => {
    if (!steps || steps.length === 0) return [];
    const out = steps.reduce((acc: Array<any>, s: any) => {
      if (acc.length === 0) {
        acc.push({ ...s });
        return acc;
      }
      const last = { ...acc[acc.length - 1] };
      const a = (last.travelMode || '').toString().toLowerCase();
      const b = (s.travelMode || '').toString().toLowerCase();
      if (a === b) {
        const merged = { ...last } as any;
        merged.durationSeconds =
          (last.durationSeconds || 0) + (s.durationSeconds || 0);
        merged.distanceMeters =
          (last.distanceMeters || 0) + (s.distanceMeters || 0);
        merged.departureTimeEpoch =
          last.departureTimeEpoch || s.departureTimeEpoch;
        merged.arrivalTimeEpoch = s.arrivalTimeEpoch || last.arrivalTimeEpoch;
        merged.instruction = [last.instruction, s.instruction]
          .filter(Boolean)
          .map((instr, index) => <p key={index}>{instr}</p>);
        merged.transitLineName = last.transitLineName || s.transitLineName;
        merged.transitLineShortName =
          last.transitLineShortName || s.transitLineShortName;
        merged.transitVehicleType =
          last.transitVehicleType || s.transitVehicleType;
        merged.departureStop = last.departureStop || s.departureStop;
        merged.arrivalStop = s.arrivalStop || last.arrivalStop;
        merged.encodedPolyline =
          last.encodedPolyline && s.encodedPolyline
            ? `${last.encodedPolyline},${s.encodedPolyline}`
            : last.encodedPolyline || s.encodedPolyline;
        acc[acc.length - 1] = merged;
      } else {
        acc.push({ ...s });
      }
      return acc;
    }, [] as Array<any>);

    return out.map((r) => ({
      ...r,
      durationText: r.durationText || formatDuration(r.durationSeconds),
    }));
  };
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState<Array<CommuteOptionDto>>([]);
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [arrivalIso, setArrivalIso] = useState<string | null>(null);
  const [departureIso, setDepartureIso] = useState<string | null>(null);
  const [hasUnfilteredResults, setHasUnfilteredResults] = useState(false);

  // Calculate estimated arrival time (start + duration)
  const estimatedArrivalTime = React.useMemo(() => {
    if (!estimatedStartTime || !estimatedDuration) return null;
    return new Date(
      new Date(estimatedStartTime).getTime() + Number(estimatedDuration)
    ).toISOString();
  }, [estimatedStartTime, estimatedDuration]);

  // Initialize timing when dropdown opens if not already set
  React.useEffect(() => {
    if (dropdownOpen && estimatedStartTime) {
      if (personalTransportFromMain) {
        // For transport FROM main: use departure time starting at estimated arrival
        if (departureIso === null) {
          const defaultTime = estimatedArrivalTime || estimatedStartTime;
          setDepartureIso(defaultTime);
        }
      }
      // For transport TO main: use arrival time (10 minutes before estimated start)
      if (arrivalIso === null) {
        const defaultTime = new Date(
          new Date(estimatedStartTime).getTime() - 10 * 60 * 1000
        ).toISOString();
        setArrivalIso(defaultTime);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dropdownOpen,
    estimatedStartTime,
    estimatedArrivalTime,
    personalTransportFromMain,
    estimatedDuration,
  ]);

  const getCommuteCall = useCommuteGetCommuteTime();
  const estimatedStartTimeHasPassed =
    estimatedStartTime && new Date(estimatedStartTime) < new Date();

  // For transport FROM main, both estimated start time and duration are required
  const missingRequiredFields = personalTransportFromMain
    ? !estimatedStartTime || !estimatedDuration
    : false;

  const disabled =
    !fromAddress ||
    !toAddress ||
    estimatedStartTimeHasPassed ||
    missingRequiredFields;
  // component for fetching and selecting commute options

  const fetchCommute = async () => {
    if (disabled) return;
    setLoading(true);
    try {
      let timeEpoch: number | undefined;
      let timeType: 'arrivalTime' | 'departureTime' = 'arrivalTime';

      if (personalTransportFromMain) {
        // For transport FROM main: use departure time
        timeType = 'departureTime';
        if (departureIso) {
          timeEpoch = Math.floor(new Date(departureIso).getTime() / 1000);
        } else if (estimatedArrivalTime) {
          timeEpoch = Math.floor(
            new Date(estimatedArrivalTime).getTime() / 1000
          );
        }
      } else {
        // For transport TO main: use arrival time
        timeType = 'arrivalTime';
        if (arrivalIso) {
          timeEpoch = Math.floor(new Date(arrivalIso).getTime() / 1000);
        } else if (estimatedStartTime) {
          timeEpoch = Math.floor(
            (new Date(estimatedStartTime).getTime() - 10 * 60 * 1000) / 1000
          );
        }
      }

      const body: any = {
        destination: toAddress,
        origin: fromAddress,
        mode: 'transit',
      };
      if (timeEpoch) body[timeType] = timeEpoch;

      const resp = (await getCommuteCall.mutateAsync({ data: body })) as any;
      // resp is CommuteOptionsResult
      const allOptions = resp.options ?? [];
      setHasUnfilteredResults(allOptions.length > 0);

      // Filter options based on timing constraints
      let filteredOptions = allOptions;

      if (timeEpoch) {
        if (personalTransportToMain) {
          // For transport TO main assignment: arrival must be on or before target time
          filteredOptions = allOptions.filter(
            (opt: any) =>
              opt.arrivalTimeEpoch && opt.arrivalTimeEpoch <= timeEpoch!
          );
        } else if (personalTransportFromMain) {
          // For transport FROM main assignment: departure must be on or after target time
          filteredOptions = allOptions.filter(
            (opt: any) =>
              opt.departureTimeEpoch && opt.departureTimeEpoch >= timeEpoch!
          );
        }
      }

      setOptions(filteredOptions);
    } catch (err: any) {
      toast.error('Kunde inte hämta restid');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    setTimeout(fetchCommute, 50);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arrivalIso, departureIso]);

  return (
    <Dropdown
      content={
        dropdownOpen && (
          <CommuteTimePickerContent>
            <div style={{ marginBottom: 16 }}>
              <div
                style={{
                  fontSize: 12,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  marginBottom: 8,
                }}
              >
                {personalTransportFromMain ? 'Önskad avresa' : 'Önskad ankomst'}
                <Input
                  id="commute-time"
                  aria-label={
                    personalTransportFromMain
                      ? 'Önskad avresa'
                      : 'Önskad ankomst'
                  }
                  type="datetime-local"
                  value={
                    personalTransportFromMain
                      ? departureIso
                        ? toInputDateTimeString(new Date(departureIso)) || ''
                        : ''
                      : arrivalIso
                        ? toInputDateTimeString(new Date(arrivalIso)) || ''
                        : ''
                  }
                  onChange={(e) => {
                    const val = e.currentTarget.value;
                    if (!val) {
                      if (personalTransportFromMain) {
                        setDepartureIso(null);
                      } else {
                        setArrivalIso(null);
                      }
                      return;
                    }
                    const iso = new Date(val).toISOString();

                    if (personalTransportToMain) {
                      // enforce constraints for transport TO main (+10 min buffer)
                      const bufferedEstimatedStartTime = estimatedStartTime
                        ? new Date(estimatedStartTime).getTime() +
                          10 * 60 * 1000
                        : null;
                      if (
                        bufferedEstimatedStartTime &&
                        new Date(iso) > new Date(bufferedEstimatedStartTime)
                      ) {
                        toast.error(
                          'Ankomst får inte vara senare än estimerad starttid + 10 minuter'
                        );
                        setArrivalIso(
                          toInputDateTimeString(
                            new Date(bufferedEstimatedStartTime)
                          )
                        );
                        return;
                      }
                      setArrivalIso(iso);
                    } else if (personalTransportFromMain) {
                      // enforce constraints for transport FROM main
                      const minDepartureTime =
                        estimatedArrivalTime || estimatedStartTime;
                      if (
                        minDepartureTime &&
                        new Date(iso) < new Date(minDepartureTime)
                      ) {
                        toast.error(
                          'Avresa får inte vara tidigare än när huvuduppdraget beräknas vara klart'
                        );
                        setDepartureIso(minDepartureTime);
                        return;
                      }
                      setDepartureIso(iso);
                    }
                  }}
                />
                <MyTextButton
                  onClick={() => setDropdownOpen(false)}
                  style={{ marginLeft: 'auto', fontSize: 18 }}
                >
                  <FontAwesomeIcon icon={faClose} />
                </MyTextButton>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <TextButton
                  onClick={() => {
                    setArrivalIso(null);
                    setDepartureIso(null);
                    setOptions([]);
                    setHasUnfilteredResults(false);
                    // Re-initialize with default time after clearing
                    setTimeout(() => {
                      if (estimatedStartTime) {
                        if (personalTransportFromMain) {
                          const defaultTime =
                            estimatedArrivalTime || estimatedStartTime;
                          setDepartureIso(defaultTime);
                        } else {
                          const defaultTime = new Date(
                            new Date(estimatedStartTime).getTime() -
                              10 * 60 * 1000
                          ).toISOString();
                          setArrivalIso(defaultTime);
                        }
                      }
                    }, 0);
                  }}
                >
                  Rensa
                </TextButton>
                <TextButton
                  onClick={() => {
                    try {
                      if (!fromAddress || !toAddress) {
                        toast('Kan inte öppna Google Maps utan adress');
                        return;
                      }
                      const url = googleMapsRoutingBaseURL
                        .replace('{origin}', encodeURIComponent(fromAddress))
                        .replace(
                          '{destination}',
                          encodeURIComponent(toAddress)
                        );
                      if (url) window.open(url, '_blank');
                      else toast('Ingen resväg tillgänglig');
                    } catch (e) {
                      toast('Kunde inte öppna Google Maps');
                    }
                  }}
                  disabled={
                    !options || options.length === 0 || !options[0]?.mapUrl
                  }
                >
                  Öppna resväg i Google Maps
                </TextButton>
              </div>
            </div>

            {options.length === 0 ? (
              <em style={{ fontSize: 12, opacity: 0.8 }}>
                {hasUnfilteredResults
                  ? 'Ingen resväg hittades som matchade tiden.'
                  : 'Inga alternativ'}
              </em>
            ) : (
              <div style={{ display: 'flex', gap: 8, flexDirection: 'column' }}>
                <div
                  style={{
                    border: '1px solid #eee',
                    padding: 6,
                    borderRadius: 4,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <TextButton
                    onClick={() => {
                      // find earlier: shift time back by 10 minutes and fetch
                      let cur: Date | null = null;

                      if (personalTransportFromMain) {
                        cur = departureIso
                          ? new Date(departureIso)
                          : estimatedArrivalTime
                            ? new Date(estimatedArrivalTime)
                            : null;
                      } else {
                        cur = arrivalIso
                          ? new Date(arrivalIso)
                          : estimatedStartTime
                            ? new Date(
                                new Date(estimatedStartTime).getTime() -
                                  10 * 60 * 1000
                              )
                            : null;
                      }

                      if (cur) {
                        const earlier = new Date(
                          cur.getTime() - 10 * 60 * 1000
                        ).toISOString();

                        if (personalTransportFromMain) {
                          setDepartureIso(earlier);
                        } else {
                          setArrivalIso(earlier);
                        }
                      }
                    }}
                  >
                    Tidigare
                  </TextButton>
                </div>
                <CommuteTimePickerOptionsWrapper>
                  {options.map((opt: any, idx: number) => {
                    return (
                      <React.Fragment key={idx}>
                        <TravelOptionWrapper>
                          <div>
                            <div style={{ fontWeight: 600 }}>
                              {getHoursAndMinutesFromMillisecondsString(
                                opt.durationSeconds * 1000
                              )}
                            </div>
                            <div style={{ fontSize: '0.9em' }}>
                              <div>
                                Avresa:{' '}
                                {opt.departureTimeEpoch
                                  ? formatDateTime(
                                      new Date(opt.departureTimeEpoch * 1000)
                                    )
                                  : '-'}{' '}
                                <br />
                                Ankomst:{' '}
                                {opt.arrivalTimeEpoch
                                  ? formatDateTime(
                                      new Date(opt.arrivalTimeEpoch * 1000)
                                    )
                                  : '-'}
                              </div>
                              <div>
                                {replaceAllTransportationModeNames(
                                  opt.description
                                )}
                              </div>
                            </div>
                          </div>
                          <div>
                            <div
                              style={{
                                display: 'flex',
                                gap: 8,
                                alignItems: 'flex-end',
                              }}
                            >
                              <MyTextButton
                                onClick={() =>
                                  setExpanded((p) => ({
                                    ...(p ?? {}),
                                    [idx]: !p[idx],
                                  }))
                                }
                              >
                                {expanded[idx] ? 'Dölj steg' : 'Visa steg'}
                              </MyTextButton>
                            </div>
                            {expanded[idx] &&
                            opt.steps &&
                            opt.steps.length > 0 ? (
                              <div
                                style={{
                                  marginTop: 6,
                                  marginBottom: 12,
                                  paddingLeft: 6,
                                }}
                              >
                                <TravelOptionStepsWrapper>
                                  {aggregateSteps(opt.steps).map(
                                    (step: any, sidx: number) => (
                                      <React.Fragment key={sidx}>
                                        <StepDiv
                                          style={{
                                            fontSize: 14,
                                            fontWeight: 600,
                                          }}
                                        >
                                          {getModeEmoji(step.travelMode)}{' '}
                                          {getModeName(step.travelMode)}{' '}
                                          {step.transitLineShortName ||
                                            step.transitLineName}
                                        </StepDiv>
                                        <StepDiv>
                                          {formatStepTimeRange(step)}
                                        </StepDiv>
                                        <StepDiv>
                                          {step.distanceMeters
                                            ? `${Math.round(step.distanceMeters)} m`
                                            : ''}
                                          {step.departureStop
                                            ? ` • ${step.departureStop}`
                                            : ''}
                                          {step.arrivalStop
                                            ? ` → ${step.arrivalStop}`
                                            : ''}
                                        </StepDiv>
                                        <TooltipInfo
                                          left={-150}
                                          info={
                                            <div style={{ width: 300 }}>
                                              {step.instruction}
                                            </div>
                                          }
                                        />
                                      </React.Fragment>
                                    )
                                  )}
                                </TravelOptionStepsWrapper>
                              </div>
                            ) : null}
                          </div>
                          <div style={{ display: 'flex', gap: 8 }}>
                            <MyTextButton
                              onClick={() => {
                                setDropdownOpen(false);
                                if (onSelectOption) {
                                  onSelectOption(opt, fromAddress, toAddress);
                                }
                              }}
                              title={opt.durationText || ''}
                            >
                              Välj
                            </MyTextButton>
                          </div>
                        </TravelOptionWrapper>
                      </React.Fragment>
                    );
                  })}
                </CommuteTimePickerOptionsWrapper>
                <div
                  style={{
                    border: '1px solid #eee',
                    padding: 6,
                    borderRadius: 4,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <TextButton
                    onClick={() => {
                      let cur: Date | null = null;

                      if (personalTransportFromMain) {
                        cur = departureIso
                          ? new Date(departureIso)
                          : estimatedArrivalTime
                            ? new Date(estimatedArrivalTime)
                            : null;
                      } else {
                        cur = arrivalIso
                          ? new Date(arrivalIso)
                          : estimatedStartTime
                            ? new Date(
                                new Date(estimatedStartTime).getTime() -
                                  10 * 60 * 1000
                              )
                            : null;
                      }

                      if (cur) {
                        const later = new Date(
                          cur.getTime() + 10 * 60 * 1000
                        ).toISOString();

                        if (personalTransportFromMain) {
                          setDepartureIso(later);
                        } else {
                          setArrivalIso(later);
                        }
                      }
                    }}
                  >
                    Senare
                  </TextButton>
                </div>
              </div>
            )}
          </CommuteTimePickerContent>
        )
      }
      onClick={() => {
        if (disabled) return;
        setDropdownOpen((o) => !o);
      }}
    >
      <TextButton
        onClick={(e) => {
          e.stopPropagation();
          if (dropdownOpen) {
            setDropdownOpen(false);
          } else {
            fetchCommute();
            setDropdownOpen(true);
          }
        }}
        disabled={disabled || loading}
      >
        {loading ? 'Hämtar...' : 'Hämta restid från Google Maps'}
      </TextButton>
      {(estimatedStartTimeHasPassed || missingRequiredFields) && (
        <TooltipInfo
          left={-100}
          hoverable
          info={
            estimatedStartTimeHasPassed
              ? 'Kan inte hämta restid eftersom den estimerade starttiden har passerat'
              : missingRequiredFields
                ? 'Kan inte hämta restid förrän huvuduppdragets estimerade starttid och varaktighet är angivna'
                : ''
          }
        />
      )}
    </Dropdown>
  );
};

export default CommuteTimePicker;
